import type {
  AgentRegistryProjectModel,
  ExternalIntegrationModel,
} from "@/api";

/**
 * The `data` payloads of the agent-registry endpoints. The generated client
 * types every `TlxHttpResponse.data` as a bare `object`, so these follow the
 * TRUST-13366 contract (`tlx-controlplane/docs/TRUST-13366-agent-registry.md`).
 */

export type RegistryCheckStatus = "PASSED" | "FAILED" | "NOT_CHECKED";

export interface RegistryCheck {
  /** e.g. PROVIDER_AUTHENTICATION, PROJECT_DISCOVERY_ACCESS, AGENT_IDENTITY_READ. */
  code: string;
  status: RegistryCheckStatus;
  message?: string;
  missingPermissions?: string[];
}

export interface RegistryCheckGroup {
  status: RegistryCheckStatus;
  checks?: RegistryCheck[];
}

export interface RegistryValidationResult {
  results?: {
    connection?: RegistryCheckGroup;
    identity?: RegistryCheckGroup & {
      provider?: string;
      credentialSource?: string;
    };
  };
  checkedAt?: string;
}

export type RegistryProject = Required<AgentRegistryProjectModel>;

export interface RegistryWarning {
  code: string;
  message: string;
}

export interface RegistryProjectPreview {
  scope?: string;
  items: RegistryProject[];
  warnings?: RegistryWarning[];
  /** The list is never partial: anything but `true` is not a usable preview. */
  complete: boolean;
  total?: number;
}

export interface RegistryImportItem {
  applicationId?: string;
  name?: string;
  externalAgentId?: string;
  projectId?: string;
  /** Set on skipped items only. */
  reason?: string;
}

export interface RegistryImportResult {
  created?: RegistryImportItem[];
  updated?: RegistryImportItem[];
  deleted?: RegistryImportItem[];
  skipped?: RegistryImportItem[];
}

export interface RegistryOnboardingResult {
  registryId: string;
  registry?: ExternalIntegrationModel;
  import?: RegistryImportResult;
}

/** The stable codes the backend puts in an error's `data`. */
export const REGISTRY_ERROR_CODES = {
  SECRET_REQUIRED: "SECRET_REQUIRED",
  SECRET_REQUIRED_FOR_IDENTITY_CHANGE: "SECRET_REQUIRED_FOR_IDENTITY_CHANGE",
  PREVIEW_INCOMPLETE: "PREVIEW_INCOMPLETE",
  PROJECT_SELECTION_CHANGED: "PROJECT_SELECTION_CHANGED",
  EMPTY_PROJECT_SELECTION: "EMPTY_PROJECT_SELECTION",
  MCP_CONFIRMATION_REQUIRED: "MCP_CONFIRMATION_REQUIRED",
  NOT_FOUND: "NOT_FOUND",
  REGISTRY_CHANGED: "REGISTRY_CHANGED",
  AGENT_LIST_INCOMPLETE: "AGENT_LIST_INCOMPLETE",
  /** The tenant has no MCP Gateway configured for the server to attest. */
  GATEWAY_REQUIRED: "GATEWAY_REQUIRED",
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
} as const;
